import React, { forwardRef, useImperativeHandle } from 'react'
import { Formik, Form, Field, FormikErrors } from 'formik'
import { IntlShape, useIntl } from 'react-intl'
import * as Yup from 'yup'

export interface TourExtra {
  _id?: string
  tourPrice: string
  type: string
  name: string
  amount: number
  frequency: 'per_person' | 'per_booking' | 'per_day' | 'one_shot' | 'per_day_person'
  applicationMode: 'PER_PERSON' | 'PER_GROUP'
  description: string
  isOptional: boolean
  createdAt?: string
  updatedAt?: string
  translations?: {
    _id?: string
    language: string
    name: string
    description: string
  }[]
}

interface ExtrasFormProps {
  initialValues: TourExtra
  onSubmit: (values: TourExtra) => void
  onCancel: () => void
  intl: IntlShape
  tourPriceId: string
  isSubmitting?: boolean
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  amount: Yup.number()
    .required('Required')
    .min(0.01, 'Amount must be greater than 0'),
  frequency: Yup.string()
    .required('Required')
    .oneOf(['per_person', 'per_booking', 'per_day', 'one_time'], 'Invalid frequency'),
  applicationMode: Yup.string()
    .required('Required')
    .oneOf(['PER_PERSON', 'PER_GROUP'], 'Invalid application mode'),
  description: Yup.string(),
  isOptional: Yup.boolean().required('Required'),
})

const ExtrasForm = forwardRef<{ handleSubmit: () => void }, ExtrasFormProps>(
  ({ initialValues, onSubmit, onCancel, intl, tourPriceId, isSubmitting }, ref) => {
    let formikRef: any = null

    useImperativeHandle(ref, () => ({
      handleSubmit: () => {
        if (formikRef) {
          formikRef.handleSubmit()
        }
      },
    }))

    return (
      <Formik
        initialValues={{
          ...initialValues,
          tourPrice: initialValues.tourPrice || tourPriceId,
          type: initialValues.type || 'extra',
          name: initialValues.name || '',
          amount: initialValues.amount || 0,
          frequency: initialValues.frequency || 'per_person',
          applicationMode: initialValues.applicationMode || 'PER_PERSON',
          description: initialValues.description || '',
          isOptional: typeof initialValues.isOptional === 'boolean' ? initialValues.isOptional : true,
          translations: initialValues.translations || [],
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
        innerRef={(formik) => {
          formikRef = formik
        }}
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched }) => {
          // Type casting for proper TypeScript typing
          const typedErrors = errors as FormikErrors<TourExtra>;
          const typedTouched = touched as { [K in keyof TourExtra]?: boolean };
          
          // Only show errors for fields that have been touched
          const shouldShowError = (fieldName: keyof TourExtra) => {
            return typedErrors[fieldName] && typedTouched[fieldName];
          };
          
          return (
            <div className='form'>
              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.EXTRAS.NAME' })}
                  </label>
                  <Field
                    type='text'
                    name='name'
                    className={`form-control form-control-solid ${
                      shouldShowError('name') ? 'is-invalid' : ''
                    }`}
                    onBlur={(e: React.FocusEvent) => {
                      setFieldTouched('name', true);
                    }}
                  />
                  {shouldShowError('name') && (
                    <div className='invalid-feedback'>{typedErrors.name as string}</div>
                  )}
                </div>

                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.EXTRAS.AMOUNT' })}
                  </label>
                  <div className='input-group'>
                    <Field
                      type='number'
                      name='amount'
                      min='0.01'
                      step='0.01'
                      className={`form-control form-control-solid ${
                        shouldShowError('amount') ? 'is-invalid' : ''
                      }`}
                      onBlur={(e: React.FocusEvent) => {
                        setFieldTouched('amount', true);
                      }}
                    />
                    <span className='input-group-text'>€</span>
                  </div>
                  {shouldShowError('amount') && (
                    <div className='invalid-feedback'>{typedErrors.amount as string}</div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.EXTRAS.FREQUENCY' })}
                  </label>
                  <Field
                    as='select'
                    name='frequency'
                    className={`form-select form-select-solid ${
                      shouldShowError('frequency') ? 'is-invalid' : ''
                    }`}
                    onBlur={(e: React.FocusEvent) => {
                      setFieldTouched('frequency', true);
                    }}
                  >
                    <option value='per_person'>
                      {intl.formatMessage({ id: 'TOUR.EXTRAS.FREQUENCY_PER_PERSON' })}
                    </option>
                    <option value='per_booking'>
                      {intl.formatMessage({ id: 'TOUR.EXTRAS.FREQUENCY_PER_BOOKING' })}
                    </option>
                    <option value='per_day'>
                      {intl.formatMessage({ id: 'TOUR.EXTRAS.FREQUENCY_PER_DAY' })}
                    </option>
                    <option value='one_shot'>
                      {intl.formatMessage({ id: 'TOUR.EXTRAS.FREQUENCY_ONE_TIME' })}
                    </option>
                    <option value='per_day_person'>
                      {intl.formatMessage({ id: 'TOUR.EXTRAS.FREQUENCY_PER_DAY_PERSON' })}
                    </option>
                  </Field>
                  {shouldShowError('frequency') && (
                    <div className='invalid-feedback'>{typedErrors.frequency as string}</div>
                  )}
                  <div className='form-text'>
                    {intl.formatMessage({ id: 'TOUR.EXTRAS.FREQUENCY_HELP' })}
                  </div>
                </div>

                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.EXTRAS.APPLICATION_MODE' })}
                  </label>
                  <Field
                    as='select'
                    name='applicationMode'
                    className={`form-select form-select-solid ${
                      shouldShowError('applicationMode') ? 'is-invalid' : ''
                    }`}
                    onBlur={(e: React.FocusEvent) => {
                      setFieldTouched('applicationMode', true);
                    }}
                  >
                    <option value='PER_PERSON'>
                      {intl.formatMessage({ id: 'TOUR.PRICING.PER_PERSON' })}
                    </option>
                    <option value='PER_TOUR'>
                      {intl.formatMessage({ id: 'TOUR.PRICING.PER_TOUR' })}
                    </option>
                  </Field>
                  {shouldShowError('applicationMode') && (
                    <div className='invalid-feedback'>{typedErrors.applicationMode as string}</div>
                  )}
                  <div className='form-text'>
                    {intl.formatMessage({ id: 'TOUR.EXTRAS.APPLICATION_MODE_HELP' })}
                  </div>
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-md-12'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.EXTRAS.DESCRIPTION' })}
                  </label>
                  <Field
                    as='textarea'
                    name='description'
                    rows='4'
                    className={`form-control form-control-solid ${
                      shouldShowError('description') ? 'is-invalid' : ''
                    }`}
                    onBlur={(e: React.FocusEvent) => {
                      setFieldTouched('description', true);
                    }}
                  />
                  {shouldShowError('description') && (
                    <div className='invalid-feedback'>{typedErrors.description as string}</div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-md-6'>
                  <div className='form-check form-switch mt-2'>
                    <Field
                      className='form-check-input'
                      type='checkbox'
                      role='switch'
                      id='isOptional'
                      name='isOptional'
                      onBlur={(e: React.FocusEvent) => {
                        setFieldTouched('isOptional', true);
                      }}
                    />
                    <label className='form-check-label ms-3' htmlFor='isOptional'>
                      {intl.formatMessage({ id: 'TOUR.EXTRAS.IS_OPTIONAL' })}
                    </label>
                  </div>
                  <div className='form-text ms-9'>
                    {intl.formatMessage({ id: 'TOUR.EXTRAS.IS_OPTIONAL_HELP' })}
                  </div>
                </div>
              </div>

              <div className='d-flex justify-content-end mt-6'>
                <button
                  type='button'
                  className='btn btn-light me-3'
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {intl.formatMessage({ id: 'TOUR.ACTIONS.CANCEL' })}
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={() => formikRef.handleSubmit()}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                  ) : null}
                  {intl.formatMessage({ id: 'TOUR.ACTIONS.SAVE' })}
                </button>
              </div>
            </div>
          )
        }}
      </Formik>
    )
  }
)

export default ExtrasForm 